import styled, { useTheme } from 'styled-components';
import {
  LogoFacebookIc,
  MailIc,
  LogoWhatsappIc,
  LinkIc,
  LogoXOutlineIc,
} from '@dsch/dd-icons';

import { BaseModal } from 'components/Toolkit/Modal/BaseModal';
import type { ModalRequiredProps } from 'components/Toolkit/Modal/BaseModal';
import { InfoTitle } from 'components/Toolkit/InfoTitle/InfoTitle';
import { copyToClipboard } from 'helpers/copyToClipboard';

export interface ShareModalProps extends ModalRequiredProps {
  isOpen: boolean;
  friendlyUrl?: string;
  trackingCallback?: (t: string) => void;
}

const SInfoTitle = styled(InfoTitle)`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

const StyledSubText = styled.span`
  ${({ theme }) => theme.fontSize.M14}
`;

const StyledText = styled.p`
  ${({ theme }) => theme.fontSize.M16}
  color: ${({ theme }) => theme.colors.GREY_DARK};
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

const Share = styled.div`
  display: flex;
  flex-direction: column;
`;

const ShareContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const SocialMediaLink = styled.a`
  display: block;
  padding: ${({ theme }) => theme.spacing.S8};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  box-shadow: ${({ theme }) => theme.shadow.CONTAINER};

  &:active {
    box-shadow: inset ${({ theme }) => theme.shadow.CONTAINER};
  }
`;

const ShareOptionContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ShareOption = styled.div`
  margin: 0 auto ${({ theme }) => theme.spacing.S4};
`;

const ShareTitle = styled.p`
  ${({ theme }) => theme.fontSize.B10}
  color: ${({ theme }) => theme.colors.GREY_DARK};
  text-align: center;
`;

const StyledButton = styled.button`
  padding: ${({ theme }) => theme.spacing.S8};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  margin-bottom: ${({ theme }) => theme.spacing.S4};
  box-shadow: ${({ theme }) => theme.shadow.CONTAINER};
  cursor: pointer;

  &:active {
    box-shadow: inset ${({ theme }) => theme.shadow.CONTAINER};
  }
`;

function ShareModal({
  isOpen,
  friendlyUrl,
  askToClose,
  trackingCallback,
}: ShareModalProps) {
  const shareMessage = 'Check out this ad on DoneDeal';
  const iconSize: number = 24;
  const theme = useTheme();
  const iconColour: string = theme.colors.GREY_DARKER;

  function handleShareClick(name: string) {
    if (trackingCallback) {
      trackingCallback(name);
    }
  }
  const infoTitleSubTitle = <StyledSubText>Help spread the word</StyledSubText>;
  return (
    <BaseModal isOpen={isOpen} askToClose={askToClose} testId="share-cad-modal">
      <SInfoTitle title="Share" listItems={[infoTitleSubTitle]} />
      <StyledText>
        Share this ad with your friends or on your favourite social media
        network
      </StyledText>

      <Share>
        <ShareContainer>
          <ShareOptionContainer>
            <ShareOption>
              <SocialMediaLink
                data-testid="whatsapp-link"
                target="_blank"
                rel="noopener"
                href={`https://wa.me/?text=${shareMessage} ${friendlyUrl}&utm_source=whatsapp&utm_medium=social`}
                onClick={() => handleShareClick('share_whatsapp')}
              >
                <LogoWhatsappIc
                  width={iconSize}
                  height={iconSize}
                  color={iconColour}
                />
              </SocialMediaLink>
            </ShareOption>
            <ShareTitle>Whatsapp</ShareTitle>
          </ShareOptionContainer>
          <ShareOptionContainer>
            <ShareOption>
              <SocialMediaLink
                data-testid="facebook-link"
                target="_blank"
                rel="noopener"
                href={`https://www.facebook.com/sharer/sharer.php?quote=${shareMessage}&u=${friendlyUrl}&utm_source=facebook&utm_medium=social`}
                onClick={() => handleShareClick('share_facebook')}
              >
                <LogoFacebookIc
                  width={iconSize}
                  height={iconSize}
                  color={iconColour}
                />
              </SocialMediaLink>
            </ShareOption>
            <ShareTitle>Facebook</ShareTitle>
          </ShareOptionContainer>
          <ShareOptionContainer>
            <ShareOption>
              <SocialMediaLink
                data-testid="email-link"
                href={`mailto:?subject=${shareMessage}&body=${shareMessage} ${friendlyUrl}&utm_source=email&utm_medium=social`}
                onClick={() => handleShareClick('share_email')}
              >
                <MailIc width={iconSize} height={iconSize} color={iconColour} />
              </SocialMediaLink>
            </ShareOption>
            <ShareTitle>Email</ShareTitle>
          </ShareOptionContainer>
          <ShareOptionContainer>
            <ShareOption>
              <StyledButton
                data-testid="copy-link"
                onClick={(event) => {
                  handleShareClick('share_copy_link');
                  friendlyUrl &&
                    copyToClipboard(event, friendlyUrl, askToClose);
                }}
              >
                <LinkIc width={iconSize} height={iconSize} color={iconColour} />
              </StyledButton>
              <ShareTitle>Copy link</ShareTitle>
            </ShareOption>
          </ShareOptionContainer>
          <ShareOptionContainer>
            <ShareOption>
              <SocialMediaLink
                data-testid="twitter-link"
                target="_blank"
                rel="noopener"
                href={`https://twitter.com/intent/tweet?url=${friendlyUrl}&text=${shareMessage}&hashtags=donedeal`}
                onClick={() => handleShareClick('share_twitter')}
              >
                <LogoXOutlineIc
                  width={iconSize}
                  height={iconSize}
                  color={iconColour}
                />
              </SocialMediaLink>
            </ShareOption>
            <ShareTitle>Twitter</ShareTitle>
          </ShareOptionContainer>
        </ShareContainer>
      </Share>
    </BaseModal>
  );
}

export { ShareModal };
